import { useState } from 'react';
import type {
  MiceChosenFlight,
  MiceNeedStatus,
  MiceNeedView,
  MiceRecommendation,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Input, ToneChip } from '@web/ui/core';
import {
  conversationEntries,
  flightLabel,
  type ConversationEntry,
  type DirectionChange,
} from '../travelers/version_diff';
import { CalcMemory } from './calc_memory';
import { ColorDot } from './color_dot';
import { formatInstant } from './need_format';
import { DrawerSection } from './need_recommendation';

/** Status que o servidor aceita reconsiderar (`POST /needs/:needId/reconsider`); `ordered` só
 *  com algum sentido sem compra viva (compra parcial), o que o servidor decide (409). */
const RECONSIDERABLE: ReadonlySet<MiceNeedStatus> = new Set<MiceNeedStatus>([
  'recommended',
  'no_solution',
  'approved',
  'ordered',
]);

const LABEL = { OUTWARD: 'mice.table.outbound', RETURN: 'mice.table.inbound' } as const;

/** O voo escolhido pelo operador sumiu: o servidor repete os voos da versão anterior com o alerta. */
const isUnavailable = (rec: MiceRecommendation): boolean =>
  rec.alerts.some((a) => a.code === 'flight_unavailable');

const labelOf = (flight: MiceChosenFlight | null): string =>
  flight ? flightLabel(flight) : t('mice.conversation.noFlight');

/** As linhas do que a revisão mudou: na primeira, os voos dela; depois, "de → para" e os sentidos
 *  que ficaram como estavam — menos na revisão indisponível, em que o aviso fala sozinho (os voos
 *  repetidos não são "sem mudança": a troca pedida não aconteceu). */
function changeLines(entry: Extract<ConversationEntry, { kind: 'agent' }>, first: boolean) {
  const { rec, diff } = entry;
  const line = ({ direction, from, to }: DirectionChange): string => {
    const label = t(LABEL[direction]);
    return first
      ? t('mice.conversation.first', { direction: label, to: labelOf(to) })
      : t('mice.conversation.changed', { direction: label, from: labelOf(from), to: labelOf(to) });
  };
  const changed = diff.filter((c) => c.from !== null || c.to !== null).map(line);
  if (first || isUnavailable(rec)) return changed;
  const unchanged = (['OUTWARD', 'RETURN'] as const)
    .filter((direction) => diff.every((c) => c.direction !== direction))
    .filter((direction) => (direction === 'OUTWARD' ? rec.outbound : rec.inbound) !== null)
    .map((direction) => t('mice.conversation.unchanged', { direction: t(LABEL[direction]) }));
  return [...changed, ...unchanged];
}

function AgentRevision({
  entry,
  first,
}: Readonly<{ entry: Extract<ConversationEntry, { kind: 'agent' }>; first: boolean }>) {
  const { rec, current } = entry;
  const unavailable = isUnavailable(rec);
  return (
    <li className="flex flex-col gap-1 rounded-lg border border-border-soft px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-micro text-muted-foreground">
        <span className="font-medium text-foreground">
          {t('mice.conversation.revision', { version: rec.version })}
        </span>
        <span className="tabular-nums">{formatInstant(rec.createdAt)}</span>
        <ColorDot color={rec.color} />
        {current ? <ToneChip tone="brand">{t('mice.conversation.current')}</ToneChip> : null}
      </div>
      <div className="flex flex-col gap-0.5 text-meta text-foreground">
        {changeLines(entry, first).map((text) => (
          <span key={text}>{text}</span>
        ))}
      </div>
      {unavailable ? (
        <p className="text-meta text-destructive">{t('mice.conversation.unavailable')}</p>
      ) : null}
      {rec.noSolution ? <p className="text-meta text-destructive">{rec.noSolution}</p> : null}
      {/* Sem decisões por sentido (recomendação antiga), o porquê é o texto da versão. */}
      {rec.directions.length === 0 ? (
        <p className="text-meta text-muted-foreground">{rec.reason}</p>
      ) : null}
      <CalcMemory rec={rec} />
    </li>
  );
}

function OperatorMessage({ text, at }: Readonly<{ text: string; at: string }>) {
  return (
    <li className="flex flex-col gap-1 rounded-lg bg-muted px-3 py-2">
      <div className="flex items-center gap-2 text-micro text-muted-foreground">
        <span className="font-medium text-foreground">{t('mice.conversation.operator')}</span>
        <span className="tabular-nums">{formatInstant(at)}</span>
      </div>
      <p className="text-meta text-foreground">{text}</p>
    </li>
  );
}

function AskForm({
  busy,
  onAsk,
}: Readonly<{ busy: boolean; onAsk: (comment: string, onDone: () => void) => void }>) {
  const [comment, setComment] = useState('');
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onAsk(comment.trim(), () => {
          setComment('');
        });
      }}
    >
      <Input
        aria-label={t('mice.conversation.field')}
        placeholder={t('mice.conversation.placeholder')}
        maxLength={500}
        value={comment}
        onChange={(e) => {
          setComment(e.target.value);
        }}
      />
      <Button size="sm" type="submit" disabled={busy || comment.trim() === ''}>
        {t('mice.conversation.ask')}
      </Button>
    </form>
  );
}

/**
 * As revisões do agente como conversa, da mais antiga para a atual, com o que o operador pediu ou
 * escolheu entre elas; no fim, o campo de pedir revisão (o `reconsider`), só para quem compra e
 * com status reconsiderável.
 */
export function AgentConversation({
  need,
  canPurchase,
  busy,
  onAsk,
}: Readonly<{
  need: MiceNeedView;
  canPurchase: boolean;
  busy: boolean;
  onAsk: (comment: string, onDone: () => void) => void;
}>) {
  const canAsk = canPurchase && RECONSIDERABLE.has(need.status);
  if (need.history.length === 0 && !canAsk) return null;
  const entries = conversationEntries(need.history);
  const firstAgent = entries.find((e) => e.kind === 'agent');
  return (
    <DrawerSection title={t('mice.conversation.title', { count: need.history.length })}>
      {entries.length > 0 ? (
        <ol className="flex flex-col gap-2.5">
          {entries.map((entry) =>
            entry.kind === 'agent' ? (
              <AgentRevision
                key={`v${String(entry.rec.version)}`}
                entry={entry}
                first={entry === firstAgent}
              />
            ) : (
              <OperatorMessage key={`${entry.at}:${entry.text}`} text={entry.text} at={entry.at} />
            ),
          )}
        </ol>
      ) : null}
      {canAsk ? <AskForm busy={busy} onAsk={onAsk} /> : null}
    </DrawerSection>
  );
}
